import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { emailConfigApi, type EmailLog, type PaginatedResponse } from 'lib/api'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { Mail, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react'
import { formatDate, cn } from 'lib/utils'

interface CandidateReceivedEmailsProps {
  candidateId: number
  refreshKey?: number
  className?: string
}

const STATUS_VARIANT: Record<EmailLog['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  sent: 'default',
  delivered: 'secondary',
  opened: 'secondary',
  failed: 'destructive',
}

export function CandidateReceivedEmails({ candidateId, refreshKey = 0, className }: CandidateReceivedEmailsProps) {
  const { t } = useTranslation()
  const [emails, setEmails] = useState<EmailLog[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const pageSize = 50

  const loadEmails = useCallback(async () => {
    setLoading(true)
    try {
      const response = await emailConfigApi.getCandidateEmails(candidateId, {
        page: 1,
        page_size: pageSize,
      })
      const data = response.data as PaginatedResponse<EmailLog> | EmailLog[]
      if (data && typeof data === 'object' && 'results' in data) {
        setEmails(data.results || [])
        setTotalCount(data.count || 0)
      } else {
        const list = Array.isArray(data) ? data : []
        setEmails(list)
        setTotalCount(list.length)
      }
    } catch (error) {
      console.error('Error loading candidate emails:', error)
      setEmails([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [candidateId, refreshKey])

  useEffect(() => {
    loadEmails()
  }, [loadEmails, refreshKey])

  return (
    <Card className={cn('flex flex-col min-h-0', className)}>
      <CardHeader className="max-[1149px]:p-4 shrink-0">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
            <Mail className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
            <span>{t('candidateDetail.receivedEmails', 'Received Emails')}</span>
            {totalCount > 0 && (
              <Badge variant="secondary" className="text-xs font-normal">
                {totalCount}
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={loadEmails} disabled={loading} className="h-8 px-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="max-[1149px]:p-4 flex-1 min-h-0 flex flex-col">
        {loading && emails.length === 0 ? (
          <div className="text-center py-4 max-[1149px]:py-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto max-[1149px]:h-6 max-[1149px]:w-6" />
            <p className="text-sm text-muted-foreground mt-2 max-[1149px]:text-xs">{t('candidateDetail.loadingEmails', 'Loading emails...')}</p>
          </div>
        ) : emails.length > 0 ? (
          <div className="space-y-3 flex-1 min-h-0 max-h-[420px] overflow-y-auto custom-scrollbar pr-1 max-[1149px]:space-y-2">
            {emails.map((email) => (
              <div key={email.id} className="p-3 border rounded-lg max-[1149px]:p-2">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="text-sm font-medium line-clamp-2 max-[1149px]:text-xs">{email.subject}</h4>
                  <Badge variant={STATUS_VARIANT[email.status] || 'outline'} className="capitalize shrink-0 max-[1149px]:text-[10px]">
                    {email.status === 'failed' ? (
                      <AlertCircle className="h-3 w-3 mr-1" />
                    ) : (
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                    )}
                    {email.status}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground max-[1149px]:text-[10px]">
                  <span>{formatDate(email.sent_at)}</span>
                  {email.sent_by_name && (
                    <span>{t('candidateDetail.emailSentBy', 'By')}: {email.sent_by_name}</span>
                  )}
                  {email.source && (
                    <span className="capitalize">{email.source}</span>
                  )}
                </div>
                {email.error_message && (
                  <p className="text-xs text-destructive mt-1 line-clamp-2" title={email.error_message}>
                    {email.error_message}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground max-[1149px]:py-6">
            <Mail className="h-12 w-12 mx-auto mb-4 opacity-50 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
            <p className="font-medium mb-2 max-[1149px]:text-sm max-[1149px]:mb-1.5">{t('candidateDetail.noEmails', 'No emails sent yet')}</p>
            <p className="text-sm max-[1149px]:text-xs">{t('candidateDetail.noEmailsDescription', 'Emails sent to this candidate will appear here')}</p>
          </div>
        )}
        {totalCount > pageSize && (
          <p className="text-xs text-muted-foreground mt-2 shrink-0">
            {t('candidateDetail.emailsShowingLatest', {
              count: pageSize,
              total: totalCount,
              defaultValue: `Showing latest ${pageSize} of ${totalCount} emails`,
            })}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
